// useUrlPrefetch — Extrai fontes de conhecimento (URLs + paths locais) de
// texto e as fetcha via IPC Electron (main process, sem CORS). Retorna
// string de contexto pronta pra injetar no system prompt do LLM antes da
// chamada.
//
// Suporta:
//   • URLs http(s) — main process bypassa CORS (fetch real)
//   • Paths absolutos (/foo/bar.md ou ~/Documents/notes.txt)
//   • file:// URLs
//
// Por que IPC e não fetch direto do renderer?
//   - Sites como google.com, cloudflare-protected, etc. bloqueiam CORS
//   - Main process não tem essas restrições (não é browser)
//   - Permite pass de User-Agent real, cookies futuros, etc.
//   - Acesso a filesystem só é possível via IPC (renderer é sandbox)

const URL_REGEX = /https?:\/\/[^\s"'<>()[\]{},;]+/g;
// Path absoluto Unix (/foo/bar) ou home (~/foo) ou file://. Não captura paths
// dentro de URLs http (regex acima já pegou).
const PATH_REGEX = /(?:^|\s)(file:\/\/[^\s"'<>()[\]{},;]+|~[^\s"'<>()[\]{},;]*|\/[^\s"'<>()[\]{},;]*\.(?:md|markdown|txt|json|csv|yaml|yml|xml|log|text))/gm;

const SKIP_EXTENSIONS = /\.(png|jpg|jpeg|gif|webp|svg|ico|pdf|zip|tar|gz|mp4|mp3|wav)$/i;

/**
 * Classifica uma string como 'url' (http/https) ou 'file' (path local/file://)
 * ou null (inválido).
 */
export function classifySource(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return 'url';
  if (trimmed.startsWith('file://')) return 'file';
  if (trimmed.startsWith('/') || trimmed.startsWith('~')) return 'file';
  return null;
}

/**
 * Extrai até `maxUrls` fontes únicas (URLs + paths) de um ou mais strings.
 * Mantém compat com nome antigo — chamadores não precisam mudar.
 */
export function extractUrls(texts, maxUrls = 5) {
  const seen = new Set();
  const results = [];
  for (const text of texts) {
    if (!text) continue;

    // URLs http(s)
    const urlMatches = text.match(URL_REGEX) || [];
    for (const raw of urlMatches) {
      const url = raw.replace(/[).,;!?]+$/, '');
      if (seen.has(url)) continue;
      if (SKIP_EXTENSIONS.test(url)) continue;
      try {
        const parsed = new URL(url);
        if (!parsed.hostname) continue;
        seen.add(url);
        results.push(url);
        if (results.length >= maxUrls) return results;
      } catch { /* invalid */ }
    }

    // Paths locais (file:// ou absolutos com extensão texto)
    const pathMatches = text.matchAll(PATH_REGEX);
    for (const m of pathMatches) {
      const p = m[1].trim();
      if (seen.has(p)) continue;
      seen.add(p);
      results.push(p);
      if (results.length >= maxUrls) return results;
    }
  }
  return results;
}

/**
 * Fetcha lista de fontes (URLs + paths) via IPC e monta string de contexto.
 * Cada item vira um bloco `<context source="..." kind="url|file">...</context>`.
 *
 * @param {string[]} sources - URLs e/ou paths locais
 * @param {object} options
 * @param {(status: string) => void} [options.onProgress]
 * @param {number} [options.maxCharsPerSource=6000]
 * @returns {Promise<string>}
 */
export async function prefetchUrls(sources, options = {}) {
  const { onProgress, maxCharsPerSource = 6000, maxCharsPerUrl } = options;
  // maxCharsPerUrl mantido por compat retroativo
  const maxChars = maxCharsPerUrl ?? maxCharsPerSource;
  if (!sources.length) return '';
  if (!window.apolo?.fetch) return '';

  const blocks = await Promise.all(
    sources.map(async (source) => {
      const kind = classifySource(source);
      if (!kind) {
        return `<context source="${source}" status="error">\nFormato não reconhecido (esperado URL http(s) ou path local)\n</context>`;
      }

      onProgress?.(`Lendo ${source}…`);

      if (kind === 'file') {
        if (!window.apolo.fetch.file) {
          return `<context source="${source}" kind="file" status="error">\napolo.fetch.file indisponível neste build\n</context>`;
        }
        const result = await window.apolo.fetch.file({ path: source, maxChars });
        if (!result.ok) {
          return `<context source="${source}" kind="file" status="error">\n${result.error}\n</context>`;
        }
        const trunc = result.truncated ? `\n[Truncado — ${result.chars} chars total]` : '';
        return `<context source="${source}" kind="file" status="ok" chars="${result.text.length}" modified="${result.modified || ''}">\n${result.text}${trunc}\n</context>`;
      }

      // kind === 'url'
      const result = await window.apolo.fetch.url({ url: source, maxChars });
      if (!result.ok) {
        return `<context source="${source}" kind="url" status="error">\n${result.error}\n</context>`;
      }
      const trunc = result.truncated ? `\n[Truncado — ${result.chars} chars total]` : '';
      // v10.3: se o fetch fez upgrade (homepage → RSS feed), reporta `feed`
      // explicitamente no header pra LLM citar a fonte certa (não a homepage).
      const upgradedAttr = result.upgradedFrom
        ? ` upgraded-from="${result.upgradedFrom}" effective-url="${result.effectiveUrl}"`
        : '';
      const kindAttr = result.kind === 'feed' ? 'feed' : 'url';
      return `<context source="${source}" kind="${kindAttr}"${upgradedAttr} status="ok" chars="${result.text.length}">\n${result.text}${trunc}\n</context>`;
    }),
  );

  return blocks.join('\n\n');
}
